import React, { useState } from 'react';
import {
  Box,
  FormHelperText,
  Grid,
  makeStyles,
  Theme,
  Typography,
} from '@material-ui/core';

import colors from '../../../themes/main-colors';
import TextField from '../../text-field';
import { SearchServiceAddress } from '../../../__generated__/pge-types';
import ServiceAddressLookup from '../../service-address-lookup/ServiceAddressLookup';
import { useTranslation } from '../../../hooks/useTranslation';
import { SelectDropdown } from '../Form/SelectDropdown';
import { FormState } from '../../../hooks/useFormState.types';
import { IQBDModel } from '../IQBDForm.rules';
import { ExpandLess, ExpandMore } from '@material-ui/icons';
import useContentMgmt from '../../../hooks/useContentMgmt';
import PhoneTextField from '../../phone-text-field';

interface ApplicantDetailsProps {
  form: FormState<IQBDModel>;
  showEmailPhoneError?: boolean;
}

const useStyles = makeStyles((theme: Theme) => ({
  applyingForBox: {
    border: `1px solid ${colors.lightGray}`,
    backgroundColor: colors.white,
    borderRadius: '16px',
    padding: '48px 0',
    width: '333px',
    textAlign: 'center',
    fontSize: '18px',
    cursor: 'pointer',
    fontFamily: 'Untitled-Sans',
    transition: 'all 0.2s',
  },
  selectedApplyingForBox: {
    background: colors.riverBlue,
    boxShadow: '0px 3px 6px #0000004D',
    fontWeight: 500,
  },
  miniApplyingForBox: {
    padding: '12px',
  },
  formSection: {
    background: `${colors.white} 0% 0% no-repeat padding-box`,
    borderRadius: '5px',
    padding: '30px 20px',
    margin: '10px 0',
  },
  sectionHeading: {
    fontFamily: 'Forma-DJR-Display',
    fontWeight: 'bold',
    fontSize: '24px',
    lineHeight: '28px',
    letterSpacing: '0.48px',
    color: colors.lightCyan,
    marginBottom: '15px',
  },
  sectionInfo: {
    fontFamily: 'Untitled-Sans',
    fontSize: '16px',
    lineHeight: '20px',
    color: colors.lightCyan,
    display: 'block',
    margin: '5px 0',
    '&:last-child': {
      marginBottom: 0,
    },
  },
  highlighted: {
    textDecoration: 'underline',
    fontWeight: 'bold',
    color: colors.sparkBlue,
    cursor: 'pointer',
    fontSize: 'inherit',
    lineHeight: 'inherit',
  },
  button: {
    borderRadius: '5px',
    padding: '13px 57px',
    fontWeight: 'bold',
    fontSize: '18px',
    fontFamily: 'Untitled-Sans',
    display: 'block',
    '&:hover': {
      boxShadow: 'none',
    },
  },
  listItem: {
    display: 'list-item',
    padding: 0,
    fontSize: '16px',
    lineHeight: '20px',
    fontFamily: 'Untitled-Sans',
    margin: '8px 0',
  },
  textfield: {
    margin: theme.spacing(0.25, 0, 0, 0),
    '& label': {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      width: '90%',
      fontSize: '16px',
      transform: 'translate(14px, 16px) scale(1)',
      '&.MuiInputLabel-shrink': {
        overflow: 'unset',
      },
    },
    '& input': {
      height: '48px',
      fontSize: '16px',
      padding: '0 14px',
    },
  },
}));

const IQBD_FORM_MICROGROUP_COPY_ID = '6wxqSYOBj1ybyYgyRPeQOA';

export const ApplicantDetails: React.FC<ApplicantDetailsProps> = props => {
  const { form } = props;
  const { content } = useContentMgmt(IQBD_FORM_MICROGROUP_COPY_ID);
  const classes = useStyles();
  const { t, richT } = useTranslation();

  return (
    <>
      <Typography css={{ marginBottom: '16px' }}>
        <strong>{content.get('APPLICANT_HEADER')}</strong>
      </Typography>

      <Grid container item spacing={2}>
        <Grid item xs={12} md={6}>
         
          <TextField
            className={classes.textfield}
            name={'applicantFirstName'}
            label={content.get('FIRST_NAME')}
            value={form.values.applicantFirstName}
            onChange={(e: any) => {
              void form.setValue('applicantFirstName', e.target.value);
            }}
          />
          {form.errors.applicantFirstName && (
            <FormHelperText error={true}>
              {form.errors.applicantFirstName}
            </FormHelperText>
          )}
        </Grid>
        <Grid item xs={12} md={6}>
         
          <TextField
            className={classes.textfield}
            name={'applicantLastName'}
            label={content.get('LAST_NAME')}
            value={form.values.applicantLastName}
            onChange={(e: any) => {
              void form.setValue('applicantLastName', e.target.value);
            }}
          />
          {form.errors.applicantLastName && (
            <FormHelperText error={true}>
              {form.errors.applicantLastName}
            </FormHelperText>
          )}
        </Grid>
      </Grid>
      <Grid
        container
        item
        spacing={2}
        css={{ marginTop: '16px', marginBottom: '16px' }}
      >
        <Grid item xs={12} md={6}>
          <Typography className={classes.sectionInfo}>
            {content.get('PHONE')}
          </Typography>
          <PhoneTextField
            name={'applicantPhone'}
            label={'Phone'}
            className={classes.textfield}
            inputProps={{ minLength: 1, maxLength: 15 }}
            data-testid="applicantPhoneF"
            {...form?.props('applicantPhone')}
          />
        </Grid>
         <Grid item xs={12} md={6}>
          <Typography className={classes.sectionInfo}>
            {content.get('EMAIL')}
          </Typography>
          <TextField
            className={classes.textfield}
            name={'applicantEmail'}
            label={content.get('REUSABLE_EMAIL')}
            value={form.values.applicantEmail}
            onChange={(e: any) => {
              void form.setValue('applicantEmail', e.target.value);
            }}
          />
          {form.errors.applicantEmail && (
            <FormHelperText error={true}>
              {form.errors.applicantEmail}
            </FormHelperText>
          )}
        </Grid>
      {props?.showEmailPhoneError && (
        <FormHelperText error={true}>
          {'* ' + content.get('EMAIL_OR_PHONE_REQUIRED')}
        </FormHelperText>
      )}
      </Grid>
    </>
  );
};
